package com.neurobs.react

import slinky.core.facade.Hooks.useContext
import slinky.core.facade.{React, ReactElement, SetStateHookCallback}


case class ServerDataAttribute[T](reactState: (Either[Option[String], T], SetStateHookCallback[Either[Option[String], T]])) {

  def value = reactState._1
  def setData(data: T) = reactState._2(Right(data))
  def setError(error: String) = reactState._2(Left(Some(error)))

  def reset() = reactState._2(Left(None))
  def loading = reactState._1 match {
    case Left(opt) => !opt.isDefined
    case Right(_) => false
  }

  def withData[U](func: T => U, noData: U) = value match {
    case Left(_) => noData
    case Right(data) => func(data)
  }

  def ConditionalElements(LoadingElement: () => ReactElement, ErrorElement: String => ReactElement, DataElement: T => ReactElement) = {
    value match {
      case Left(errorOpt) => errorOpt match {
        case None => LoadingElement()
        case Some(error) => ErrorElement(error)
      }
      case Right(data) => DataElement(data)
    }
  }

}

object ServerDataAttribute {
  def emptyDefault[T]: ServerDataAttribute[T] = ServerDataAttribute[T]((Left(None), new SetStateHookCallback[Either[Option[String], T]](_ => {})))
}

case class RegisteredChangeAttribute(reactState: (Int, SetStateHookCallback[Int])) {
  def register() = reactState._2.apply(reactState._1 + 1)
}

object RegisteredChangeAttribute {
  def emptyDefault: RegisteredChangeAttribute = RegisteredChangeAttribute(0, new SetStateHookCallback[Int](_ => {}))
}

case class BooleanAttribute(reactState: (Boolean, SetStateHookCallback[Boolean])) {
  def value: Boolean = reactState._1
  def setValue(bool: Boolean) = reactState._2(bool)
  def toggle() = setValue(!value)
}

object BooleanAttribute {
  def emptyDefault: BooleanAttribute = BooleanAttribute(false, new SetStateHookCallback[Boolean](_ => {}))
}

abstract class GenericState {

  def voidState[T](stateAttribute: StateAttribute[Option[T]]) = stateAttribute.setValue(None)
  def setOptionState[T](stateAttribute: StateAttribute[Option[T]], value: T) = stateAttribute.setValue(Some(value))
  def setState[T](stateAttribute: StateAttribute[T], value: T) = stateAttribute.setValue(value)

}

abstract trait GenericStateGenerator[U <: GenericState ] {

  def generateEmptyDefaultState(): U

  val stateContext = React.createContext[U](generateEmptyDefaultState())

  def Provider(initialState: U) = {
    stateContext.Provider(value = initialState)
  }

  def accessState: U = useContext(stateContext)

  def emptyOptionDefaultState[T]() = StateAttribute.emptyDefault[Option[T]](None)
  def emptyDefaultState[T](emptyVal: T) = StateAttribute.emptyDefault[T](emptyVal)
  def emptyRegisteredChangeState() = RegisteredChangeAttribute.emptyDefault
  def emptyBooleanState() = BooleanAttribute.emptyDefault
  def emptyServerDataState[T]() = ServerDataAttribute.emptyDefault[T]

}
